import {
  CLEANING_SERVICE_SET_UP_PATH,
  CONSUMABLES_ONBOARDING_SET_PATH,
  CUSTOMER_REQUESTS_PATH,
  TENANT_ADMIN_BILLING_PATH,
  TENANT_ADMIN_BUILDINGS_PATH,
  TENANT_ADMIN_TEAM_PATH
} from 'routes/paths';
import { BuildingUnitTenancy, ConsumablesProductSubscription } from 'shared';
import { CleaningServiceSchedule } from 'store/CleaningService/types';
import { Member } from 'store/Tenancy/types';
import { BuildingUnit } from 'store/Tenancy/types';
import { BillingContact } from 'store/Tenancy/types';
import {
  AccessPath,
  InsuranceRequirementsDocument,
  TenantCoi,
  TenantFloorplan,
  TenantLease
} from 'ux/Tenant/TenantAdmin/Building/types';
import { hasPublishedVendorAccessForBuildingUnit } from 'ux/Tenant/TenantAdmin/Building/utils';
import { TenantOnboardingChecklistItem } from 'ux/Tenant/TenantAdmin/requests';
import { TenancyService } from 'ux/Tenant/TenantAdmin/types';

import {
  CHECKLIST_KEY_ADD_BUILDING_CONTACTS,
  CHECKLIST_KEY_ADD_VENDOR_ACCESS_INSTRUCTIONS,
  CHECKLIST_KEY_CONFIRM_BILLING_CONTACTS,
  CHECKLIST_KEY_CONFIRM_POC,
  CHECKLIST_KEY_PICK_INTERNET_PLAN,
  CHECKLIST_KEY_SELECT_IT_SERVICES,
  CHECKLIST_KEY_SETUP_CLEANING,
  CHECKLIST_KEY_SETUP_PANTRY,
  CHECKLIST_KEY_SHARE_ISP_NETWORK_DETAILS,
  CHECKLIST_KEY_START_OFFICE_MANUAL,
  CHECKLIST_KEY_UPLOAD_COI,
  CHECKLIST_KEY_UPLOAD_INSURANCE_REQUIREMENTS,
  CHECKLIST_KEY_UPLOAD_FLOORPLAN,
  CHECKLIST_KEY_UPLOAD_LEASE
} from './checklistKeys';
import { ChecklistData, ChecklistItemState, ChecklistItemStatus } from './checklistTypes';
import { CHECKLIST_DATA } from './checklistTypes';

interface ChecklistStateData {
  members: Member[];
  buildingUnits: BuildingUnit[];
  buildingUnitTenancies: BuildingUnitTenancy[];
  tenancyLeases: TenantLease[] | null;
  tenancyCois: TenantCoi[] | null;
  tenancyFloorplans: TenantFloorplan[] | null;
  tenancyInsuranceRequirementsDocuments: InsuranceRequirementsDocument[] | null;
  billingContacts: BillingContact[] | null | undefined;
  cleaningServiceSchedules: CleaningServiceSchedule[];
  consumablesProductSubscriptions: ConsumablesProductSubscription[] | null;
  tenancyServices: TenancyService[] | null;
  supportTickets: Array<{ description?: string | null }>;
  skippedChecklistItems?: TenantOnboardingChecklistItem[];
  accessPaths?: AccessPath[] | null;
}

export const deriveChecklistStates = (data: ChecklistStateData): ChecklistData => {
  const {
    members,
    buildingUnits,
    buildingUnitTenancies,
    tenancyCois,
    tenancyFloorplans,
    tenancyInsuranceRequirementsDocuments,
    billingContacts,
    cleaningServiceSchedules,
    consumablesProductSubscriptions,
    supportTickets,
    skippedChecklistItems = [],
    accessPaths = null
  } = data;

  // Helper function to check if item is skipped from API data
  const isItemSkippedFromAPI = (itemKey: string): boolean => {
    const skippedItem = skippedChecklistItems.find(item => item.key === itemKey);
    return skippedItem !== undefined && skippedItem.skipped_at !== null;
  };

  // Helper function to get state for an item
  const getItemState = (itemKey: string): ChecklistItemState => {
    // First determine the natural state based on data
    let naturalState: ChecklistItemState = 'default';

    switch (itemKey) {
      case CHECKLIST_KEY_CONFIRM_POC:
        naturalState = members.some(member => member.is_main_onsite_contact === true) ? 'completed' : 'default';
        break;

      case CHECKLIST_KEY_UPLOAD_LEASE:
        // Check if there's a primary lease linked to the building unit tenancy
        // A lease is considered complete if it's set as the lease_id on a building unit tenancy
        naturalState = buildingUnitTenancies.some(but => but.lease_id !== null && but.lease_id !== undefined)
          ? 'completed'
          : 'default';
        break;

      case CHECKLIST_KEY_ADD_BUILDING_CONTACTS:
        naturalState = buildingUnits.some(unit => unit.building_users && unit.building_users.length > 0)
          ? 'completed'
          : 'default';
        break;

      case CHECKLIST_KEY_CONFIRM_BILLING_CONTACTS:
        naturalState = billingContacts && billingContacts.length > 0 ? 'completed' : 'default';
        break;

      case CHECKLIST_KEY_ADD_VENDOR_ACCESS_INSTRUCTIONS: {
        // Complete when ANY of the tenancy's units has published vendor access — checking only
        // buildingUnits[0] disagreed with the admin Vendor Access tab (and the migration, which
        // attaches paths to the workspace's unit, not necessarily the first) for multi-unit tenancies.
        naturalState = buildingUnits.some(unit => hasPublishedVendorAccessForBuildingUnit(accessPaths, unit.id))
          ? 'completed'
          : 'default';
        break;
      }

      case CHECKLIST_KEY_UPLOAD_COI:
        // Check if there are COI documents uploaded
        naturalState = tenancyCois && tenancyCois.length > 0 ? 'completed' : 'default';
        break;

      case CHECKLIST_KEY_UPLOAD_FLOORPLAN:
        // Check if there are floorplan documents uploaded
        naturalState = tenancyFloorplans && tenancyFloorplans.length > 0 ? 'completed' : 'default';
        break;

      case CHECKLIST_KEY_UPLOAD_INSURANCE_REQUIREMENTS:
        // Check if there are insurance requirements documents uploaded
        naturalState =
          tenancyInsuranceRequirementsDocuments && tenancyInsuranceRequirementsDocuments.length > 0
            ? 'completed'
            : 'default';
        break;

      case CHECKLIST_KEY_START_OFFICE_MANUAL: {
        const hasNonEmptyValue = (value: string | undefined) => {
          const trimmed = value?.trim() ?? '';
          if (trimmed === '') return false;
          const emptyPlaceholders = ['<p></p>', '<p><br></p>', '<p><br/></p>'];
          return !emptyPlaceholders.includes(trimmed);
        };

        naturalState = buildingUnits.some(unit => {
          const allInstructions = [...(unit.building_instructions ?? []), ...(unit.building_unit_instructions ?? [])];
          return allInstructions.some(i => hasNonEmptyValue(i.value));
        })
          ? 'completed'
          : 'default';
        break;
      }

      case CHECKLIST_KEY_PICK_INTERNET_PLAN: {
        // Only handle when there IS an active_or_upcoming internet configuration
        const firstBuildingUnitTenancy = buildingUnitTenancies.length > 0 ? buildingUnitTenancies[0] : null;
        const internetConfig = firstBuildingUnitTenancy?.active_or_upcoming_internet_configuration;

        if (!internetConfig) {
          // If no active/upcoming config, this item shouldn't be shown (handled by CHECKLIST_KEY_SHARE_ISP_NETWORK_DETAILS)
          naturalState = 'default';
          break;
        }

        const buttonDisabled = getInternetPlanButtonDisabled(buildingUnitTenancies);
        if (buttonDisabled) {
          naturalState = 'blocked';

          break;
        }

        // If selected quote and checked out, it's completed
        if (internetConfig.selected_internet_quote_id && internetConfig.checked_out_at) {
          naturalState = 'completed';
        } else {
          // Otherwise it's in progress (not blocked)
          naturalState = 'default';
        }
        break;
      }

      case CHECKLIST_KEY_SELECT_IT_SERVICES:
        // Check if there's a support ticket with IT services description
        naturalState = supportTickets.some(
          ticket => ticket.description && ticket.description.toLowerCase().includes('additional it services')
        )
          ? 'completed'
          : 'default';
        break;

      case CHECKLIST_KEY_SHARE_ISP_NETWORK_DETAILS: {
        // Only handle when there is NO active_or_upcoming internet configuration
        const firstBuildingUnitTenancy = buildingUnitTenancies.length > 0 ? buildingUnitTenancies[0] : null;
        const internetConfig = firstBuildingUnitTenancy?.active_or_upcoming_internet_configuration;

        if (internetConfig) {
          // If there's an active/upcoming config, this item shouldn't be shown (handled by CHECKLIST_KEY_PICK_INTERNET_PLAN)
          naturalState = 'default';
          break;
        }

        // No active_or_upcoming config - check for unmanaged internet details
        const hasUnmanagedDetails =
          firstBuildingUnitTenancy?.unmanaged_internet_details &&
          firstBuildingUnitTenancy.unmanaged_internet_details.length > 0;
        const hasWifiNetworks =
          hasUnmanagedDetails &&
          firstBuildingUnitTenancy.unmanaged_internet_details.some(
            detail => detail.wifi_networks && detail.wifi_networks.length > 0
          );

        if (hasWifiNetworks) {
          naturalState = 'completed';
        } else {
          naturalState = 'default';
        }
        break;
      }

      case CHECKLIST_KEY_SETUP_CLEANING:
        naturalState = cleaningServiceSchedules && cleaningServiceSchedules.length > 0 ? 'completed' : 'default';
        break;

      case CHECKLIST_KEY_SETUP_PANTRY:
        naturalState =
          consumablesProductSubscriptions && consumablesProductSubscriptions.length > 0 ? 'completed' : 'default';
        break;

      default:
        naturalState = 'default';
    }

    // If item is completed, return completed
    if (naturalState === 'completed') {
      return 'completed';
    }

    // Check if item is skipped from API data first (only if not completed)
    if (isItemSkippedFromAPI(itemKey)) {
      return 'skipped';
    }

    return naturalState;
  };

  // Helper function to get button text for an item
  const getButtonText = (itemKey: string, state: ChecklistItemState): string | undefined => {
    if (state === 'completed') {
      return 'View';
    }
    return undefined; // Use default from CHECKLIST_DATA
  };

  // Helper function to get button type for an item
  const getButtonType = (itemKey: string, state: ChecklistItemState): 'secondaryOutline' | undefined => {
    if (state === 'completed') {
      return 'secondaryOutline';
    }
    return undefined; // Use default from CHECKLIST_DATA
  };

  // Helper function to get description (returns undefined if should use default from CHECKLIST_DATA)
  const getItemDescription = (itemKey: string, buildingUnitTenancies: BuildingUnitTenancy[]): string | undefined => {
    if (itemKey === CHECKLIST_KEY_PICK_INTERNET_PLAN) {
      return getInternetPlanDescription(buildingUnitTenancies);
    }
    return undefined; // Use default from CHECKLIST_DATA
  };

  // Helper function to get status
  const getItemStatus = (
    itemKey: string,
    buildingUnitTenancies: BuildingUnitTenancy[]
  ): ChecklistItemStatus | undefined => {
    if (itemKey === CHECKLIST_KEY_PICK_INTERNET_PLAN) {
      const firstBuildingUnitTenancy = buildingUnitTenancies.length > 0 ? buildingUnitTenancies[0] : null;
      const internetConfig = firstBuildingUnitTenancy?.active_or_upcoming_internet_configuration;

      if (internetConfig) {
        const hasQuotes = internetConfig.internet_quotes && internetConfig.internet_quotes.length > 0;
        const allQuotesPublished =
          hasQuotes && internetConfig.internet_quotes.every(quote => quote.is_published === true);

        if (!hasQuotes || !allQuotesPublished) {
          return 'waiting-on-codi';
        }
      }
      return undefined;
    }
    return undefined; // Use default from CHECKLIST_DATA
  };

  // Helper function to get route for an item (handles completed state routes)
  const getItemRoute = (
    itemKey: string,
    state: ChecklistItemState,
    buildingId: number | undefined,
    _buildingUnitTenancies: BuildingUnitTenancy[]
  ): string | undefined => {
    if (!buildingId) return undefined;

    // Handle completed state routes
    if (state === 'completed') {
      switch (itemKey) {
        case CHECKLIST_KEY_CONFIRM_POC:
          return TENANT_ADMIN_TEAM_PATH;

        case CHECKLIST_KEY_UPLOAD_LEASE:
        case CHECKLIST_KEY_UPLOAD_COI:
        case CHECKLIST_KEY_UPLOAD_FLOORPLAN:
        case CHECKLIST_KEY_UPLOAD_INSURANCE_REQUIREMENTS:
          return `${TENANT_ADMIN_BUILDINGS_PATH}/${buildingId}/documents`;

        case CHECKLIST_KEY_ADD_BUILDING_CONTACTS:
          return buildingId ? `${TENANT_ADMIN_BUILDINGS_PATH}/${buildingId}/contacts` : undefined;

        case CHECKLIST_KEY_CONFIRM_BILLING_CONTACTS:
          return TENANT_ADMIN_BILLING_PATH;

        case CHECKLIST_KEY_ADD_VENDOR_ACCESS_INSTRUCTIONS:
          return buildingId ? `${TENANT_ADMIN_BUILDINGS_PATH}/${buildingId}/access` : undefined;

        case CHECKLIST_KEY_START_OFFICE_MANUAL:
          return buildingId ? `${TENANT_ADMIN_BUILDINGS_PATH}/${buildingId}/office-manual` : undefined;

        case CHECKLIST_KEY_SELECT_IT_SERVICES:
          return CUSTOMER_REQUESTS_PATH;

        case CHECKLIST_KEY_SETUP_CLEANING:
          return CLEANING_SERVICE_SET_UP_PATH;

        case CHECKLIST_KEY_SETUP_PANTRY:
          return CONSUMABLES_ONBOARDING_SET_PATH;

        case CHECKLIST_KEY_PICK_INTERNET_PLAN:
        case CHECKLIST_KEY_SHARE_ISP_NETWORK_DETAILS:
          return buildingId ? `${TENANT_ADMIN_BUILDINGS_PATH}/${buildingId}/internet` : undefined;

        default:
          return undefined;
      }
    }

    // Routes for non-completed items are handled in ChecklistPage.tsx handleItemStart
    return undefined;
  };

  // Helper function to get dynamic description for internet plan item (only called when there IS an active/upcoming config)
  const getInternetPlanDescription = (buildingUnitTenancies: BuildingUnitTenancy[]): string => {
    const firstBuildingUnitTenancy = buildingUnitTenancies.length > 0 ? buildingUnitTenancies[0] : null;
    const internetConfig = firstBuildingUnitTenancy?.active_or_upcoming_internet_configuration;

    if (!internetConfig) {
      return "We're working on getting you quotes to select your internet service.";
    }

    const hasQuotes = internetConfig.internet_quotes && internetConfig.internet_quotes.length > 0;
    const allQuotesPublished = hasQuotes && internetConfig.internet_quotes.every(quote => quote.is_published === true);

    if (!hasQuotes) {
      return "We're working on getting you quotes to select your internet service.";
    }

    if (allQuotesPublished && !internetConfig.checked_out_at) {
      return 'Your quotes are ready — compare providers, select your plan, and get connected.';
    }

    if (internetConfig.selected_internet_quote_id && internetConfig.checked_out_at) {
      return 'Your quotes are ready — compare providers, select your plan, and get connected.';
    }

    return "We're working on getting you quotes to select your internet service.";
  };

  // Helper function to get button disabled state for internet plan item (only called when there IS an active/upcoming config)
  const getInternetPlanButtonDisabled = (buildingUnitTenancies: BuildingUnitTenancy[]): boolean => {
    const firstBuildingUnitTenancy = buildingUnitTenancies.length > 0 ? buildingUnitTenancies[0] : null;
    const internetConfig = firstBuildingUnitTenancy?.active_or_upcoming_internet_configuration;

    if (!internetConfig) {
      return false;
    }

    const hasQuotes = internetConfig.internet_quotes && internetConfig.internet_quotes.length > 0;
    const allQuotesPublished = hasQuotes && internetConfig.internet_quotes.every(quote => quote.is_published === true);

    // Button should be disabled if there are no quotes OR if there are quotes but not all are published
    return !hasQuotes || !allQuotesPublished;
  };

  // Helper function to get timestamp from API data
  const getTimestamp = (
    itemKey: string,
    currentState: ChecklistItemState,
    type: 'completed' | 'skipped'
  ): string | undefined => {
    // For skipped items, get timestamp from API data
    if (type === 'skipped' && currentState === 'skipped') {
      const skippedItem = skippedChecklistItems.find(item => item.key === itemKey);
      if (skippedItem && skippedItem.skipped_at) {
        return skippedItem.skipped_at;
      }
    }

    // For completed items, we don't have timestamps from API yet
    // Return undefined for now
    return undefined;
  };

  // Get buildingId from first building unit for building-related routes
  const buildingId = buildingUnits.length > 0 ? buildingUnits[0].building_id : undefined;

  // Check if there's an active/upcoming internet config to determine which item to show
  const firstBuildingUnitTenancy = buildingUnitTenancies.length > 0 ? buildingUnitTenancies[0] : null;
  const internetConfigForFiltering = firstBuildingUnitTenancy?.active_or_upcoming_internet_configuration;
  const hasActiveOrUpcomingInternetConfig = internetConfigForFiltering != null;

  // Map over CHECKLIST_DATA and update states
  const checklistData = CHECKLIST_DATA.map(section => {
    // Filter items in the internet-it section based on whether there's an active/upcoming config
    let filteredItems = section.items;
    if (section.id === 'internet-it') {
      filteredItems = section.items.filter(item => {
        if (item.key === CHECKLIST_KEY_PICK_INTERNET_PLAN) {
          // Only show this item when there IS an active/upcoming config
          return hasActiveOrUpcomingInternetConfig;
        }
        if (item.key === CHECKLIST_KEY_SHARE_ISP_NETWORK_DETAILS) {
          // Only show this item when there is NO active/upcoming config
          return !hasActiveOrUpcomingInternetConfig;
        }
        return true; // Keep all other items
      });
    }

    return {
      ...section,
      items: filteredItems.map(item => {
        const state = getItemState(item.key);
        const buttonText = getButtonText(item.key, state);
        const buttonType = getButtonType(item.key, state);
        const description = getItemDescription(item.key, buildingUnitTenancies);
        const status = getItemStatus(item.key, buildingUnitTenancies);
        const viewRoute = getItemRoute(item.key, state, buildingId, buildingUnitTenancies);
        const completedAt = getTimestamp(item.key, state, 'completed');
        const skippedAt = getTimestamp(item.key, state, 'skipped');

        return {
          ...item,
          state,
          ...(description !== undefined ? { description } : {}),
          ...(buttonText !== undefined ? { startButtonText: buttonText } : {}),
          ...(buttonType !== undefined ? { buttonType } : {}),
          ...(status !== undefined ? { status } : {}),
          ...(viewRoute !== undefined ? { viewRoute } : {}),
          ...(completedAt !== undefined ? { completedAt } : {}),
          ...(skippedAt !== undefined ? { skippedAt } : {})
        };
      })
    };
  });

  return checklistData;
};

export const hasDefaultChecklistItems = (checklistData: ChecklistData): boolean => {
  return checklistData.some(section => section.items.some(item => item.state === 'default'));
};
